import React, { useEffect, useState } from "react";
import DatePicker from "../components/common/DatePicker";
import SelectField from "../components/common/SelectField";
import { registerSchema } from "../validators/validatorSchema";
import { clearError, validateData } from "../validators/YupValidations";
import API from "../config/axios";
import {
  objectToFormData,
  toastErrorMessage,
  toastSuccessMessage,
} from "../utils/utils";
import { api_paths } from "../config/api.paths";
import { useDispatch } from "react-redux";
import { SET_LOADING, SET_USER } from "../redux/user/index";
import OtpModal from "../components/auth/OtpModal";
import { useNavigate } from "react-router-dom";
import RegisterMembership from "./membership/RegisterMembership";
import { Button, InputGroup } from "react-bootstrap";
import { IoEyeOutline, IoEyeOffOutline } from "react-icons/io5";
import { formatUSPhone, normalizePhoneForSave } from "../utils/phoneFormat";

const Field = ({ label, required, children }) => (
  <div className="mb-3 pb-1">
    <label className="form-label small mb-2">
      {label} {required && <span className="text-danger">*</span>}
    </label>
    {children}
  </div>
);

const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    first_name: "",
    middle_name: "",
    last_name: "",
    date_of_birth: null,
    email: "",
    country_code: "+1",
    phone: "",
    gender: "",
    marital_status: null,
    address: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      zip: "",
      country: "",
    },
    password: "",
    confirm_password: "",
    newsletter: true,
    terms: false,
  });
  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [otpModal, setOtpModal] = useState(false);
  const [otpId, setOtpId] = useState(null);
  const [showRegisterMembership, setShowRegisterMembership] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const genderOptions = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "other", label: "Other" },
  ];

  const maritalStatusOptions = [
    { value: "single", label: "Single" },
    { value: "married", label: "Married" },
    { value: "divorced", label: "Divorced" },
    { value: "widowed", label: "Widowed" },
  ];

  const countryCodeOptions = [
    { value: "+1", label: "+1" },
    { value: "+91", label: "+91" },
  ];

  const handleRegister = async (e) => {
    e.preventDefault();

    const formForValidation = {
      ...formData,
      phone:
        formData.country_code === "+1"
          ? normalizePhoneForSave(formData.phone)
          : formData.phone.replace(/\D/g, ""),
    };

    const isValid = await validateData(
      registerSchema,
      formForValidation,
      setErrors
    );
    if (!isValid) return;

    // Build submission payload without mutating state
    const submission = {
      ...formForValidation, // normalized phone
    };
    delete submission.confirm_password;
    delete submission.terms;

    const tempFormData = new FormData();
    objectToFormData(tempFormData, submission);

    try {
      // SEND OTP API
      dispatch(SET_LOADING(true));
      const response = await API.post(api_paths.auth.REGISTER, tempFormData);

      if (response.data?.payload?.result) {
        setOtpId(response.data.payload.result);
        setOtpModal(true);
        toastSuccessMessage(response?.data?.message);
      }
    } catch (error) {
      setFormData({
        ...formData,
        confirm_password: "",
        terms: false,
      });
      toastErrorMessage(error?.response?.data?.message);
    } finally {
      dispatch(SET_LOADING(false));
    }
  };

  const handleOtpVerify = async (otp) => {
    try {
      dispatch(SET_LOADING(true));
      const response = await API.post(api_paths.auth.VERIFY_OTP, {
        otp,
        otp_id: otpId,
      });

      if (response.data?.payload?.result) {
        const result = response.data.payload.result;

        dispatch(SET_USER(result.memberDoc));
        localStorage.setItem("GSTB_token", result.token);
        API.defaults.headers.common["Authorization"] = "Bearer " + result.token;

        toastSuccessMessage(response?.data?.message);
        setOtpModal(false);
        setOtpId(null);
        setShowRegisterMembership(true);
        navigate("/membership", { state: { showRegisterMembership: true } });
      }
    } catch (error) {
      toastErrorMessage(error?.response?.data?.message);
    } finally {
      dispatch(SET_LOADING(false));
    }
  };

  const handleResendOtp = async (otp_id) => {
    try {
      dispatch(SET_LOADING(true));
      const response = await API.post(
        `${api_paths.auth.RESEND_OTP}?type=register`,
        {
          otp_id,
        }
      );

      if (response.data?.payload?.result) {
        toastSuccessMessage(response?.data?.message);
      }
    } catch (error) {
      toastErrorMessage(error?.response?.data?.message);
    } finally {
      dispatch(SET_LOADING(false));
    }
  };

  const getCountry = async () => {
    dispatch(SET_LOADING(true));
    try {
      const response = await API.get(
        `${api_paths.address.GET_COUNTRY}?pagination=false`
      );
      if (response.data?.payload?.data) {
        const list = response.data.payload.data;
        setCountries(list);

        // auto-select FIRST country
        if (list.length > 0) {
          setFormData((prev) => ({
            ...prev,
            address: {
              ...prev.address,
              country: list[0]._id, // set country ID
              state: "", // reset state
              city: "", // reset city
            },
          }));

          // load states for this country
          getState(list[0]._id);
        }
      }
    } catch (error) {
      console.error("Failed to fetch country:", error);
    } finally {
      dispatch(SET_LOADING(false));
    }
  };

  const getState = async (countryId) => {
    dispatch(SET_LOADING(true));
    try {
      const response = await API.get(
        `${api_paths.address.GET_STATE}?country_id=${countryId}&pagination=false`
      );

      if (response.data?.payload?.data) {
        const list = response.data.payload.data;
        setStates(list);
        setCities([]);

        // reset state + city
        setFormData((prev) => ({
          ...prev,
          address: {
            ...prev.address,
            state: "", // reset state
            city: "", // reset city
          },
        }));
      }
    } catch (error) {
      console.error("Failed to fetch state:", error);
    } finally {
      dispatch(SET_LOADING(false));
    }
  };

  const getCity = async (stateId) => {
    dispatch(SET_LOADING(true));
    try {
      const response = await API.get(
        `${api_paths.address.GET_CITY}?state_id=${stateId}&pagination=false`
      );

      if (response.data?.payload?.data) {
        const list = response.data.payload.data;
        setCities(list);
        setFormData((prev) => ({
          ...prev,
          address: {
            ...prev.address,
            city: "", // reset city
          },
        }));
      }
    } catch (error) {
      console.error("Failed to fetch city:", error);
    } finally {
      dispatch(SET_LOADING(false));
    }
  };

  useEffect(() => {
    getCountry();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      {showRegisterMembership ? (
        <RegisterMembership />
      ) : (
        <>
          {/* Your Registration form */}
          <div className="registration-page py-4">
            <div className="container">
              <div className="register-header mb-4">
                <h1 className="text-center inter-bold h1">
                  Membership Registration
                </h1>
                <p className="text-center">Join Gujarati Samaj Tampa Bay</p>
              </div>

              <div className="row justify-content-center py-3">
                <div className="col-lg-9">
                  <div className="register-card p-4 position-relative">
                    {/* decorative glow on right */}
                    <div className="decor-glow" aria-hidden />

                    <form onSubmit={handleRegister}>
                      <div className="row gx-4 p-2">
                        {/* First Name */}
                        <div className="col-12 col-md-4">
                          <Field label="First Name" required>
                            <input
                              type="text"
                              placeholder="Enter first name"
                              className={`form-control form-control-sm ${
                                errors.first_name ? "is-invalid" : ""
                              }`}
                              value={formData.first_name}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  first_name: e.target.value,
                                });
                                errors.first_name &&
                                  clearError(setErrors, "first_name");
                              }}
                            />
                            {errors.first_name && (
                              <div className="invalid-feedback">
                                {errors.first_name.message}
                              </div>
                            )}
                          </Field>
                        </div>

                        {/* Middle Name */}
                        <div className="col-12 col-md-4">
                          <Field label="Middle Name">
                            <input
                              type="text"
                              placeholder="Enter middle name"
                              className={`form-control form-control-sm ${
                                errors.middle_name ? "is-invalid" : ""
                              }`}
                              value={formData.middle_name}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  middle_name: e.target.value,
                                });
                                errors.middle_name &&
                                  clearError(setErrors, "middle_name");
                              }}
                            />
                            {errors.middle_name && (
                              <div className="invalid-feedback">
                                {errors.middle_name.message}
                              </div>
                            )}
                          </Field>
                        </div>

                        {/* Last Name */}
                        <div className="col-12 col-md-4">
                          <Field label="Last Name" required>
                            <input
                              type="text"
                              placeholder="Enter last name"
                              className={`form-control form-control-sm ${
                                errors.last_name ? "is-invalid" : ""
                              }`}
                              value={formData.last_name}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  last_name: e.target.value,
                                });
                                errors.last_name &&
                                  clearError(setErrors, "last_name");
                              }}
                            />
                            {errors.last_name && (
                              <div className="invalid-feedback">
                                {errors.last_name.message}
                              </div>
                            )}
                          </Field>
                        </div>

                        {/* DOB */}
                        {/* <div className="col-12 col-md-12">
                          <Field label="Date of Birth" required>
                            <DatePicker
                              value={formData.date_of_birth}
                              onChange={(date) => {
                                setFormData({
                                  ...formData,
                                  date_of_birth: date,
                                });
                                errors.date_of_birth &&
                                  clearError(setErrors, "date_of_birth");
                              }}
                              error={errors.date_of_birth?.message}
                            />
                          </Field>
                        </div> */}

                        {/* Email */}
                        <div className="col-12 col-md-6">
                          <Field label="Email" required>
                            <input
                              type="email"
                              placeholder="Enter email"
                              className={`form-control form-control-sm ${
                                errors.email ? "is-invalid" : ""
                              }`}
                              value={formData.email}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  email: e.target.value,
                                });
                                errors.email && clearError(setErrors, "email");
                              }}
                            />
                            {errors.email && (
                              <div className="invalid-feedback">
                                {errors.email.message}
                              </div>
                            )}
                          </Field>
                        </div>

                        {/* Phone */}
                        <div className="col-12 col-md-6">
                          <Field label="Mobile Number" required>
                            <div className="d-flex gap-2">
                              {/* Country Code Select */}
                              <div style={{ width: "120px" }}>
                                <SelectField
                                  value={countryCodeOptions.find(
                                    (opt) => opt.value === formData.country_code
                                  )}
                                  options={countryCodeOptions}
                                  onChange={(opt) => {
                                    const newCode = opt.value;

                                    setFormData((prev) => ({
                                      ...prev,
                                      country_code: newCode,
                                      phone:
                                        newCode === "+1"
                                          ? formatUSPhone(
                                              normalizePhoneForSave(prev.phone)
                                            )
                                          : prev.phone.replace(/\D/g, ""),
                                    }));

                                    errors.phone &&
                                      clearError(setErrors, "phone");
                                  }}
                                />
                              </div>

                              {/* Phone Number Input */}
                              <input
                                type="tel"
                                placeholder="Enter number"
                                className={`form-control form-control-sm ${
                                  errors.phone ? "is-invalid" : ""
                                }`}
                                value={formData.phone}
                                onChange={(e) => {
                                  let value = e.target.value;

                                  if (formData.country_code === "+1") {
                                    const digits = value
                                      .replace(/\D/g, "")
                                      .slice(0, 10);
                                    value = formatUSPhone(digits);
                                  } else {
                                    value = value
                                      .replace(/\D/g, "")
                                      .slice(0, 15);
                                  }

                                  setFormData({ ...formData, phone: value });
                                  errors.phone &&
                                    clearError(setErrors, "phone");
                                }}
                                maxLength={
                                  formData.country_code === "+1" ? 14 : 15
                                }
                              />
                            </div>

                            {errors.phone && (
                              <div className="invalid-feedback d-block">
                                {errors.phone.message}
                              </div>
                            )}
                          </Field>
                        </div>

                        {/* Gender */}
                        <div className="col-12 col-md-4 col-lg-4">
                          <Field label="Gender" required>
                            <SelectField
                              value={genderOptions.find(
                                (opt) => opt.value === formData.gender
                              )}
                              options={genderOptions}
                              onChange={(opt) => {
                                setFormData({ ...formData, gender: opt.value });
                                errors.gender &&
                                  clearError(setErrors, "gender");
                              }}
                              error={errors.gender?.message}
                            />
                          </Field>
                        </div>

                        {/* Marital Status */}
                        <div className="col-12 col-md-4 col-lg-4">
                          <Field label="Marital Status">
                            <SelectField
                              value={maritalStatusOptions.find(
                                (opt) => opt.value === formData.marital_status
                              )}
                              options={maritalStatusOptions}
                              onChange={(opt) => {
                                setFormData({
                                  ...formData,
                                  marital_status: opt.value,
                                });
                                errors.marital_status &&
                                  clearError(setErrors, "marital_status");
                              }}
                              error={errors.marital_status?.message}
                            />
                          </Field>
                        </div>

                        {/* DOB */}
                        <div className="col-12 col-md-4 col-lg-4">
                          <Field label="Date of Birth" required>
                            <DatePicker
                              value={formData.date_of_birth}
                              futureNotAllowed={true}
                              onChange={(date) => {
                                setFormData({
                                  ...formData,
                                  date_of_birth: date,
                                });
                                errors.date_of_birth &&
                                  clearError(setErrors, "date_of_birth");
                              }}
                              error={errors.date_of_birth?.message}
                            />
                          </Field>
                        </div>
                        {/* Address 1 */}
                        <div className="col-12 col-md-6">
                          <Field label="Address Line 1" required>
                            <input
                              type="text"
                              placeholder="Enter address"
                              className={`form-control form-control-sm ${
                                errors["address.line1"] ? "is-invalid" : ""
                              }`}
                              value={formData.address.line1}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  address: {
                                    ...formData.address,
                                    line1: e.target.value,
                                  },
                                });
                                errors["address.line1"] &&
                                  clearError(setErrors, "address.line1");
                              }}
                            />

                            {errors["address.line1"] && (
                              <div className="invalid-feedback d-block">
                                {errors["address.line1"].message}
                              </div>
                            )}
                          </Field>
                        </div>

                        {/* Address 2 */}
                        <div className="col-12 col-md-6">
                          <Field label="Address Line 2">
                            <input
                              type="text"
                              placeholder="Enter address"
                              className="form-control form-control-sm"
                              value={formData.address.line2}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  address: {
                                    ...formData.address,
                                    line2: e.target.value,
                                  },
                                });
                                errors["address.line2"] &&
                                  clearError(setErrors, "address.line2");
                              }}
                            />
                          </Field>
                        </div>

                        {/* Country */}
                        <div className="col-12 col-md-6">
                          <Field label="Country" required>
                            <SelectField
                              value={countries
                                .map((c) => ({ value: c._id, label: c.name }))
                                .find(
                                  (o) => o.value === formData.address.country
                                )}
                              options={countries.map((c) => ({
                                value: c._id,
                                label: c.name,
                              }))}
                              onChange={(opt) => {
                                setFormData({
                                  ...formData,
                                  address: {
                                    ...formData.address,
                                    country: opt.value,
                                    state: "",
                                    city: "",
                                  },
                                });
                                errors["address.country"] &&
                                  clearError(setErrors, "address.country");
                                getState(opt.value);
                              }}
                              error={errors["address.country"]?.message}
                            />
                          </Field>
                        </div>

                        {/* State */}
                        <div className="col-12 col-md-6">
                          <Field label="State" required>
                            <SelectField
                              value={
                                formData.address.state
                                  ? states
                                      .map((s) => ({
                                        value: s._id,
                                        label: s.name,
                                      }))
                                      .find(
                                        (o) =>
                                          o.value === formData.address.state
                                      )
                                  : null
                              }
                              options={states.map((s) => ({
                                value: s._id,
                                label: s.name,
                              }))}
                              onChange={(opt) => {
                                setFormData({
                                  ...formData,
                                  address: {
                                    ...formData.address,
                                    state: opt.value,
                                    city: "",
                                  },
                                });
                                errors["address.state"] &&
                                  clearError(setErrors, "address.state");
                                getCity(opt.value);
                              }}
                              error={errors["address.state"]?.message}
                            />
                          </Field>
                        </div>

                        {/* City */}
                        <div className="col-12 col-md-6">
                          <Field label="City" required>
                            <SelectField
                              value={
                                formData.address.city
                                  ? cities
                                      .map((c) => ({
                                        value: c._id,
                                        label: c.name,
                                      }))
                                      .find(
                                        (o) => o.value === formData.address.city
                                      )
                                  : null
                              }
                              options={cities.map((c) => ({
                                value: c._id,
                                label: c.name,
                              }))}
                              onChange={(opt) => {
                                setFormData({
                                  ...formData,
                                  address: {
                                    ...formData.address,
                                    city: opt.value,
                                  },
                                });
                                errors["address.city"] &&
                                  clearError(setErrors, "address.city");
                              }}
                              error={errors["address.city"]?.message}
                            />
                          </Field>
                        </div>

                        {/* Zip */}
                        <div className="col-12 col-md-6">
                          <Field label="Zip Code" required>
                            <input
                              type="text"
                              placeholder="Enter zip code"
                              className={`form-control form-control-sm ${
                                errors["address.zip"] ? "is-invalid" : ""
                              }`}
                              value={formData.address.zip}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  address: {
                                    ...formData.address,
                                    zip: e.target.value,
                                  },
                                });
                                errors["address.zip"] &&
                                  clearError(setErrors, "address.zip");
                              }}
                            />

                            {errors["address.zip"] && (
                              <div className="invalid-feedback d-block">
                                {errors["address.zip"].message}
                              </div>
                            )}
                          </Field>
                        </div>

                        {/* Password */}
                        <div className="col-12 col-md-6">
                          {/* <Field label="Password" required>
                            <input
                              type="password"
                              placeholder="Enter password"
                              className={`form-control form-control-sm ${
                                errors.password ? "is-invalid" : ""
                              }`}
                              value={formData.password}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  password: e.target.value,
                                });
                                errors.password && clearError(setErrors, "password");
                              }}
                            />
                            {errors.password && (
                              <div className="invalid-feedback">{errors.password.message}</div>
                            )}
                          </Field> */}
                          <Field label="Password" required>
                            <InputGroup>
                              <input
                                type={showPassword ? "text" : "password"}
                                placeholder="Enter password"
                                className={`form-control form-control-sm ${
                                  errors.password ? "is-invalid" : ""
                                }`}
                                value={formData.password}
                                onChange={(e) => {
                                  setFormData({
                                    ...formData,
                                    password: e.target.value,
                                  });
                                  if (errors.password)
                                    clearError(setErrors, "password");
                                }}
                                aria-label="Password"
                              />
                              {/* react-bootstrap v2 removed InputGroup.Append — place Button directly */}
                              <Button
                                type="button"
                                className="toggle-password"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setShowPassword((s) => !s);
                                }}
                                aria-pressed={showPassword}
                                aria-label={
                                  showPassword
                                    ? "Hide password"
                                    : "Show password"
                                }
                                tabIndex={0}
                              >
                                {showPassword ? (
                                  <IoEyeOffOutline />
                                ) : (
                                  <IoEyeOutline />
                                )}
                              </Button>
                              {errors.password && (
                                <div className="invalid-feedback d-block">
                                  {errors.password.message}
                                </div>
                              )}
                            </InputGroup>
                          </Field>
                        </div>

                        {/* Confirm Password */}
                        <div className="col-12 col-md-6">
                          <Field label="Confirm Password" required>
                            <InputGroup>
                              <input
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder="Re-enter password"
                                className={`form-control form-control-sm ${
                                  errors.confirm_password ? "is-invalid" : ""
                                }`}
                                value={formData.confirm_password}
                                onChange={(e) => {
                                  setFormData({
                                    ...formData,
                                    confirm_password: e.target.value,
                                  });
                                  if (errors.confirm_password)
                                    clearError(setErrors, "confirm_password");
                                }}
                              />

                              <Button
                                type="button"
                                variant="outline-secondary"
                                onClick={() =>
                                  setShowConfirmPassword((prev) => !prev)
                                }
                                className="toggle-password"
                              >
                                {showConfirmPassword ? (
                                  <IoEyeOffOutline />
                                ) : (
                                  <IoEyeOutline />
                                )}
                              </Button>
                            </InputGroup>

                            {errors.confirm_password && (
                              <div className="invalid-feedback d-block">
                                {errors.confirm_password.message}
                              </div>
                            )}
                          </Field>
                        </div>

                        {/* Checkboxes */}
                        <div className="col-12 my-2">
                          <div className="form-check mb-2 d-flex">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="subscribe"
                              checked={formData.newsletter}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  newsletter: e.target.checked,
                                })
                              }
                            />
                            <label
                              className="form-check-label small"
                              htmlFor="subscribe"
                            >
                              I would like to subscribe to the Newsletter
                              updates
                            </label>
                          </div>

                          <div className="form-check mt-2 d-flex">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="terms"
                              checked={formData.terms}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  terms: e.target.checked,
                                });

                                // remove error on change
                                errors.terms && clearError(setErrors, "terms");
                              }}
                            />

                            <label
                              className="form-check-label small"
                              htmlFor="terms"
                            >
                              I accept the Membership's{" "}
                              <a
                                href="/privacy-policy"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ textDecoration: "underline" }}
                              >
                                Privacy Policy
                              </a>
                              {" And "}
                              <a
                                href="/terms-and-conditions"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ textDecoration: "underline" }}
                              >
                                Terms and Conditions*
                              </a>
                            </label>

                            {errors.terms && (
                              <div className="text-danger small mt-1">
                                {errors.terms.message}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Buttons (centered like screenshot) */}
                        <div className="col-12 d-flex justify-content-start gap-3 mt-4">
                          <button
                            type="button"
                            className="btn btn-light btn-lg border"
                            onClick={() => navigate("/")}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="btn btn-lg btn-orange px-4"
                          >
                            Register
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
            <OtpModal
              isOpen={otpModal}
              onClose={() => setOtpModal(false)}
              onVerify={handleOtpVerify}
              onResend={handleResendOtp}
              otp_id={otpId}
            />
          </div>
        </>
      )}
    </>
  );
};

export default Register;
